import Button from "@/shared/ui/components/button";
import Checkbox from "@/shared/ui/components/checkbox";
import MoneyAmount from "@/shared/ui/components/money-amount";
import type { AppContext } from "@/types";
import { LOCALIZED_ICON_TYPE } from "@/types";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Processing } from "../types";
import {
  emitPaymentSubmissionTracking,
  emitDocumentTracking,
  emitModalTracking,
} from "@/shared/tracking/analytics";
import * as events from "@/events";
import { logError } from "@/services/logging/logger";
import { PaymentMethodUtils } from "../services/payment-method-utils";
import type { CheckoutSession, PaymentOption } from "@/types";
import { agreeFinancingAgreementsLabelKey } from "@/lib/payment-option";

const CARD_ID_NEW = "new";

interface PaymentFooterProps {
  ctx: AppContext;
  session: CheckoutSession;
  isAgreed: boolean;
  onAgreeChange: (agreed: boolean) => void;
  /** Murabaha (non–KFS) document checkbox row + sheet; hidden when `documents` has no non-KFS items. */
  showMurabahaDocumentSigningCheckbox: boolean;
  isWalletSelected: boolean;
  selectedPaymentMethod?: { id?: string; type?: string };
  /** When true, Apple Pay must not count as a ready payment method (e.g. Pay Next Month). */
  isPnm?: boolean;
  /** UAE BNPL+ uses financing agreement copy instead of Murabaha. */
  isUaeBnplPlus?: boolean;
  documentSignatureIsPending: boolean;
  processing: Processing;
  isProcessingPayment: boolean;
  submittingGoBack: boolean;
  onPayClick: () => Promise<void>;
  onDocumentsClick: () => void;
  showError: boolean;
  validateCardForm?: () => boolean;
}

function getPaymentMethodTypeForTracking(
  selectedPaymentMethod: PaymentFooterProps["selectedPaymentMethod"],
  isWalletSelected: boolean,
  canWalletCover: boolean,
): "apple_pay" | "wallet_only" | "card" {
  if (isWalletSelected && canWalletCover) return "wallet_only";
  if ((selectedPaymentMethod?.type ?? "") === "apple_pay") return "apple_pay";
  return "card";
}

function getPayButtonLabel(
  ctx: AppContext,
  selectedPaymentOption: PaymentOption | undefined,
  isWalletSelected: boolean,
  t: AppContext["t"],
  i18n: { language: string },
) {
  if (selectedPaymentOption?.tenure === 1) {
    return <span>{t("payment_confirmation.complete_order_pay_later")}</span>;
  }
  if (
    isWalletSelected &&
    ctx.session.paymentDetails?.canWalletCoverDownPayment
  ) {
    return <span>{t("payment_confirmation.pay_with_wallet")}</span>;
  }
  const amount =
    isWalletSelected && ctx.session.paymentDetails?.cardDownPaymentAmountWithWallet
      ? ctx.session.paymentDetails.cardDownPaymentAmountWithWallet.amount
      : ctx.selectedPayment().amountToPayNow;
  return (
    <>
      {t("payment_confirmation.pay")}{" "}
      <MoneyAmount
        amount={amount}
        currency={ctx.session.order.currency}
        language={i18n.language}
      />
    </>
  );
}

/**
 * Component for payment footer with submit button
 * Single responsibility: Rendering payment footer UI and handling payment submission
 */
export function PaymentFooter({
  ctx,
  session,
  isAgreed,
  onAgreeChange,
  showMurabahaDocumentSigningCheckbox,
  isWalletSelected,
  selectedPaymentMethod,
  isPnm = false,
  isUaeBnplPlus = false,
  documentSignatureIsPending,
  processing,
  isProcessingPayment,
  onPayClick,
  onDocumentsClick,
  showError,
  validateCardForm,
}: PaymentFooterProps) {
  const { i18n } = useTranslation();
  const { t } = ctx;
  const agreementLabel = t(agreeFinancingAgreementsLabelKey(isUaeBnplPlus));
  const openSheetAriaLabel = t("documents.title");

  const handleDocumentsClick = useCallback(() => {
    ctx.sendEvent(events.VIEW_FINANCE_DOCUMENTS_CLICKED);
    emitDocumentTracking("finance_documents_viewed", session);
    emitModalTracking("documents_modal_opened", session, {
      modal_name: "documents",
    });
    onDocumentsClick();
  }, [ctx, session, onDocumentsClick]);

  if (!session) {
    return null;
  }

  const selectedPaymentOption = (session.paymentOptions ?? []).find(
    (option) => option.offerId === (session.selectedPaymentOptionId ?? ""),
  );

  const selectedCardIsNew = selectedPaymentMethod?.id === CARD_ID_NEW;
  const hasValidCardForm = validateCardForm?.() ?? false;

  const applePayMustNotSubmit =
    isPnm && (selectedPaymentMethod?.type ?? "") === "apple_pay";

  const canSubmitPayment =
    (selectedPaymentMethod !== undefined && !applePayMustNotSubmit) ||
    (isWalletSelected &&
      session.paymentDetails?.canWalletCoverDownPayment === true) ||
    hasValidCardForm;

  const handlePayClick = async () => {
    try {
      ctx.sendEvent(events.CONTINUE_CLICKED, {
        continue_from: "confirmation",
        document_signature_is_pending: documentSignatureIsPending,
        is_wallet_selected: isWalletSelected,
        payment_details: JSON.stringify(ctx.session.paymentDetails),
      });

      if (documentSignatureIsPending) {
        return;
      }

      if (processing !== Processing.NONE) return;

      const paymentMethodType = getPaymentMethodTypeForTracking(
        selectedPaymentMethod,
        isWalletSelected,
        Boolean(ctx.session.paymentDetails?.canWalletCoverDownPayment),
      );
      const amountToPay = PaymentMethodUtils.calculateAmountToPay(
        ctx,
        isWalletSelected,
      );

      emitPaymentSubmissionTracking("payment_submission_started", session, {
        payment_method_type: paymentMethodType,
        card_id:
          selectedPaymentMethod?.id !== CARD_ID_NEW
            ? selectedPaymentMethod?.id
            : undefined,
        wallet_selected: isWalletSelected,
        amount: amountToPay,
        document_signature_pending: documentSignatureIsPending,
      });

      await onPayClick();
    } catch (error) {
      logError(
        "payment-footer",
        "Error processing payment",
        error instanceof Error ? error : new Error(String(error)),
        { error },
      );
    }
  };

  return (
    <div className="relative" data-testid="payment-footer">
      <div className="space-y-4 flex flex-col items-center">
        {showMurabahaDocumentSigningCheckbox && (
          <>
            <div className="flex items-center space-x-3 mb-6" data-testid="document-signing-container">
              <label className="inline-flex items-center space-x-3 cursor-pointer select-none">
                <input type="checkbox" className="hidden peer" />
                <div data-testid="document-signing-checkbox">
                  <Checkbox
                    value={isAgreed}
                    onChange={() => {
                      const newValue = !isAgreed;
                      onAgreeChange(newValue);
                      emitDocumentTracking(
                        newValue ? "terms_agreed" : "terms_disagreed",
                        session,
                      );
                    }}
                  />
                </div>
              </label>
              <span
                className="text-sm text-[#16181D] font-medium leading-tight text-left flex items-center gap-2"
              >
                <span>{agreementLabel}</span>
                <button
                  type="button"
                  onClick={handleDocumentsClick}
                  className="text-[#7E82AD] hover:text-gray-800 transition-colors cursor-pointer"
                  data-testid="view-documents-link"
                  aria-label={openSheetAriaLabel}
                >
                  {ctx.localizedIcon(LOCALIZED_ICON_TYPE.CHEVRON_FORWARD)}
                </button>
              </span>
            </div>
            {showError && (
              <p className="text-red-500 text-xs text-center" data-testid="document-agree-error">
                {t("payment_confirmation.please_agree")}
              </p>
            )}
          </>
        )}

        <Button
          onClick={handlePayClick}
          disabled={
            (selectedCardIsNew && !hasValidCardForm) ||
            !canSubmitPayment ||
            processing === Processing.SUBMIT ||
            documentSignatureIsPending
          }
          className="w-full h-[54px] py-3 rounded-full font-semibold hover:bg-purple-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
          loading={processing === Processing.SUBMIT}
          data-testid="pay-button"
        >
          {isProcessingPayment ? (
            <div className="flex items-center justify-center space-x-2 text-[#7E82AD]">
              <span>{t("payment_confirmation.processing_payment")}</span>
            </div>
          ) : (
            <div className="flex justify-center gap-2">
              {getPayButtonLabel(
                ctx,
                selectedPaymentOption,
                isWalletSelected,
                t,
                i18n,
              )}
            </div>
          )}
        </Button>
      </div>
    </div>
  );
}
